import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { addNotification } from '../store/slices/uiSlice';

/**
 * Shared admin Excel batch helpers (export / template / import with confirm modal).
 */
export default function useExcelBatch({
  exportUrl,
  templateUrl,
  importUrl,
  templateFilename,
  exportFilenamePrefix,
  exportParams = {},
  upsertKey = null,
  getExistingKeys,
  labels = {},
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);

  const L = {
    exportSuccess: labels.exportSuccess || '匯出成功',
    exportError: labels.exportError || '匯出失敗',
    templateDownloaded: labels.templateDownloaded || '已下載匯入範本',
    templateError: labels.templateError || '下載範本失敗',
    invalidFileType: labels.invalidFileType || '請上傳 Excel 檔案 (.xlsx)',
    emptyFile: labels.emptyFile || '檔案沒有資料列',
    importError: labels.importError || '匯入失敗',
    importing: labels.importing || '匯入中...',
    importSuccess: labels.importSuccess,
  };

  const handleExport = async () => {
    try {
      const response = await api.get(exportUrl, {
        params: exportParams,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${exportFilenamePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(addNotification({ type: 'success', message: L.exportSuccess }));
    } catch (err) {
      console.error('Export failed:', err);
      dispatch(addNotification({ type: 'error', message: L.exportError }));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(templateUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', templateFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(addNotification({ type: 'success', message: L.templateDownloaded }));
    } catch (err) {
      console.error('Template download failed:', err);
      dispatch(addNotification({ type: 'error', message: L.templateError }));
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      dispatch(addNotification({ type: 'error', message: L.invalidFileType }));
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (rows.length < 2) {
        dispatch(addNotification({ type: 'error', message: L.emptyFile }));
        return;
      }

      const totalRows = rows.length - 1;
      let toUpdate = 0;
      let toCreate = totalRows;

      if (upsertKey && getExistingKeys) {
        const headers = rows[0].map((h) => String(h).trim());
        const keyIdx = headers.indexOf(upsertKey);
        const fileKeys = rows
          .slice(1)
          .map((row) => String(row[keyIdx] || '').trim())
          .filter((id) => id.length > 0);
        const existingIds = new Set(await getExistingKeys());
        toUpdate = fileKeys.filter((id) => existingIds.has(id)).length;
        toCreate = totalRows - toUpdate;
      }

      setImportPreview({ toCreate, toUpdate, totalRows, file });
    } catch (err) {
      console.error('Preview failed:', err);
      dispatch(addNotification({ type: 'error', message: L.importError }));
    }
  };

  const handleConfirmImport = async (onSuccess) => {
    if (!importPreview?.file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importPreview.file);
    setImportPreview(null);
    try {
      const response = await api.post(importUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const msg = L.importSuccess
        ? L.importSuccess(response.data)
        : `匯入完成：新增 ${response.data.created}，更新 ${response.data.updated}，錯誤 ${response.data.errors}`;
      dispatch(addNotification({ type: 'success', message: msg }));
      if (onSuccess) onSuccess(response.data);
    } catch (err) {
      console.error('Import failed:', err);
      const detail = err.response?.data?.detail;
      const safeMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || String(d)).join('; ')
        : typeof detail === 'string'
          ? detail
          : L.importError;
      dispatch(addNotification({ type: 'error', message: safeMsg }));
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => setImportPreview(null);

  return {
    fileInputRef,
    importing,
    importPreview,
    handleExport,
    handleDownloadTemplate,
    handleImportClick,
    handleFileChange,
    handleConfirmImport,
    handleCancelImport,
  };
}

export function ExcelBatchButtons({ batch, importingLabel = '匯入中...', showLabels = true }) {
  return (
    <>
      <button
        type="button"
        onClick={batch.handleExport}
        className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1"
      >
        <i className="fas fa-file-export"></i> {showLabels ? '匯出' : ''}
      </button>
      <button
        type="button"
        onClick={batch.handleDownloadTemplate}
        className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1"
      >
        <i className="fas fa-file-download"></i> {showLabels ? '範本' : ''}
      </button>
      <button
        type="button"
        onClick={batch.handleImportClick}
        disabled={batch.importing}
        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1 disabled:opacity-50"
      >
        <i className="fas fa-file-import"></i>{' '}
        {batch.importing ? importingLabel : showLabels ? '匯入' : ''}
      </button>
      <input
        ref={batch.fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={batch.handleFileChange}
        className="hidden"
      />
    </>
  );
}

export function ExcelImportConfirmModal({ batch, onConfirm }) {
  if (!batch.importPreview) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-yellow-100 p-2 rounded-full">
            <i className="fas fa-file-import text-yellow-600 text-xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">確認批量匯入</h3>
        </div>
        <p className="text-gray-600 text-sm mb-4">請確認以下匯入摘要後繼續。</p>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">總列數</span>
            <span className="font-semibold text-gray-900">{batch.importPreview.totalRows}</span>
          </div>
          <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
            <span className="text-sm text-green-700">
              <i className="fas fa-plus-circle mr-1"></i>將新增
            </span>
            <span className="font-semibold text-green-700 text-lg">{batch.importPreview.toCreate}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-blue-700">
              <i className="fas fa-edit mr-1"></i>將更新
            </span>
            <span className="font-semibold text-blue-700 text-lg">{batch.importPreview.toUpdate}</span>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={batch.handleCancelImport}
            className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => batch.handleConfirmImport(onConfirm)}
            disabled={batch.importing}
            className="px-5 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-medium transition-colors disabled:opacity-50"
          >
            {batch.importing ? '匯入中...' : '確認匯入'}
          </button>
        </div>
      </div>
    </div>
  );
}
